import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { LINK_ADDRESS_RULE, readLinkAddress } from './linkAddress';
import styles from './LinkDialog.module.css';

interface LinkDialogProps {
  /** The address of the link under the selection, if there is one. */
  initialHref?: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called once the dialog has gone, with how it closed. Nothing opens it
   * through a Radix trigger, so Radix cannot give focus back by itself: the
   * editor puts it back in the text after a change, and on the link button
   * otherwise.
   */
  onClosed?: (outcome: 'changed' | 'dismissed') => void;
  onRemove: () => void;
  onSubmit: (href: string) => void;
}

/**
 * The text editor's link dialog, in place of the browser's own prompt.
 */
export function LinkDialog({
  initialHref,
  isOpen,
  onClose,
  onClosed,
  onRemove,
  onSubmit,
}: LinkDialogProps) {
  const [address, setAddress] = useState(initialHref ?? '');
  const [refused, setRefused] = useState(false);
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const outcomeRef = useRef<'changed' | 'dismissed'>('dismissed');
  const isExistingLink = Boolean(initialHref);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    outcomeRef.current = 'dismissed';
    setAddress(initialHref ?? '');
    setRefused(false);
  }, [initialHref, isOpen]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const href = readLinkAddress(address);

    if (!href) {
      setRefused(true);
      fieldRef.current?.focus();
      return;
    }

    outcomeRef.current = 'changed';
    onSubmit(href);
  }

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      open={isOpen}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          aria-describedby={undefined}
          className={styles.content}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onClosed?.(outcomeRef.current);
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            fieldRef.current?.focus();
            fieldRef.current?.select();
          }}
        >
          <Dialog.Title className={styles.title}>Insert link</Dialog.Title>

          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <label className={styles.label} htmlFor={fieldId}>
              Link address
            </label>
            <input
              aria-describedby={refused ? `${errorId} ${hintId}` : hintId}
              aria-invalid={refused ? 'true' : undefined}
              autoCapitalize="off"
              autoComplete="off"
              className={styles.input}
              id={fieldId}
              inputMode="url"
              onChange={(event) => {
                setAddress(event.target.value);
                setRefused(false);
              }}
              placeholder="https://"
              ref={fieldRef}
              spellCheck={false}
              type="text"
              value={address}
            />
            <p className={styles.hint} id={hintId}>
              A web address, an email address with mailto:, or a path on this site.
            </p>
            {refused ? (
              <p className={styles.error} id={errorId} role="alert">
                {LINK_ADDRESS_RULE}
              </p>
            ) : null}

            <div className={styles.actions}>
              {isExistingLink ? (
                <button
                  className={styles.removeAction}
                  onClick={() => {
                    outcomeRef.current = 'changed';
                    onRemove();
                  }}
                  type="button"
                >
                  Remove link
                </button>
              ) : null}
              <Dialog.Close asChild>
                <button className={styles.secondaryAction} type="button">
                  Cancel
                </button>
              </Dialog.Close>
              <button className={styles.primaryAction} type="submit">
                {isExistingLink ? 'Update' : 'Insert'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
